import { ScenarioSchema, type Equipment, type Scenario, type Stage, STAGES, STAGE_LABEL } from "../schema/scenario";
import type { MethodConfig } from "../schema/master";
import { conversionFactor, denominatorUnit } from "../schema/units";
import { factorsForMethod, findFactor, resolveFactor, toApplied } from "./factors";
import { fmt, pct } from "./format";
import { MASS_BALANCE_RELATIVE_TOLERANCE as MASS_TOL, ELECTRICITY_RECONCILIATION_TOLERANCE } from "./rules";
import { mismatchedProductionEquipment } from "./scenario-ops";
import type {
  CalcContext,
  CalcLine,
  CalcResult,
  CalcStep,
  CertResult,
  Contributor,
  ElectricityResult,
  EquipmentResult,
  Issue,
} from "./types";

/** 수소 저위발열량 MJ/kg , 에너지 기준 배분에 쓴다 */
export const H2_LHV_MJ_PER_KG = 120;

/** 방식 규칙을 적용한 실제 경계 포함 여부 */
export function effectiveInBoundary(eq: Equipment, method: MethodConfig): { inBoundary: boolean; forcedOut: boolean } {
  if (method.boundary.locked && !method.boundary.includeLocations.includes(eq.location)) {
    return { inBoundary: false, forcedOut: true };
  }
  return { inBoundary: eq.inBoundary, forcedOut: false };
}

/** 전력 믹스 계수 합성 */
export function composeElectricity(scenario: Scenario, ctx: CalcContext, issues: Issue[], totalKWh: number): ElectricityResult {
  const methodFactors = factorsForMethod(ctx.factors, scenario.method);
  const rows = scenario.electricity.sources.map((s) => {
    const src = ctx.sources.find((x) => x.id === s.sourceId && x.active);
    if (!src) {
      issues.push({ level: "error", code: "MIX_SOURCE_UNKNOWN", message: `알 수 없는 전력원: ${s.sourceId}`, step: "electricity" });
      return { sourceId: s.sourceId, label: s.sourceId, share: 0, ef: 0, efId: "", kWh: 0, factor: null, raw: s };
    }
    const f = findFactor(methodFactors, src.efId) ?? findFactor(ctx.factors, src.efId);
    if (!f) issues.push({ level: "error", code: "NO_EF", message: `${src.label} 의 배출계수(${src.efId})를 찾을 수 없습니다`, step: "electricity" });
    return { sourceId: src.id, label: src.label, share: s.share, ef: f?.value ?? 0, efId: f?.id ?? src.efId, kWh: s.kWh ?? 0, factor: f ? toApplied(f) : null, raw: s };
  });

  const enteredKWh = scenario.electricity.mode === "kWh" ? rows.reduce((sum, row) => sum + row.kWh, 0) : null;
  let shares: number[];
  if (scenario.electricity.mode === "kWh") {
    const sum = rows.reduce((a, r) => a + r.kWh, 0);
    shares = rows.map((r) => (sum > 0 ? r.kWh / sum : 0));
    if (sum <= 0) issues.push({ level: "error", code: "MIX_SHARE_SUM", message: "전력원별 사용량이 모두 0 입니다", step: "electricity" });
    else if (Math.abs(sum - totalKWh) > ELECTRICITY_RECONCILIATION_TOLERANCE * Math.max(totalKWh, 1)) {
      issues.push({ level: "warning", code: "MIX_QUANTITY_MISMATCH", message: `전력원 비율 산정용 입력 합계 ${fmt(sum)} kWh와 경계 내 설비 전력량 ${fmt(totalKWh)} kWh가 다릅니다. 입력량은 전력원 비율에만 사용됩니다. 측정기간·경계와 차이 근거를 확인하세요${scenario.electricity.note?.trim() ? ` (기록한 근거: ${scenario.electricity.note.trim()})` : ""}`, step: "electricity" });
    }
  } else {
    const sum = rows.reduce((a, r) => a + r.share, 0);
    if (Math.abs(sum - 1) > 0.001) {
      issues.push({
        level: sum > 0 ? "warning" : "error",
        code: "MIX_SHARE_SUM",
        message: `전력원 비율 합계가 ${pct(sum)} 입니다. 100% 가 되도록 맞춰 주세요${sum > 0 ? " (계산에는 합계로 나눈 비율을 적용)" : ""}`,
        step: "electricity",
      });
    }
    shares = rows.map((r) => (sum > 0 ? r.share / sum : 0));
  }
  const mixEf = rows.reduce((a, r, i) => a + (shares[i] ?? 0) * r.ef, 0);
  return {
    mixEf,
    totalKWh,
    enteredKWh,
    sources: rows.map((r, i) => ({ sourceId: r.sourceId, label: r.label, share: shares[i] ?? 0, ef: r.ef, efId: r.efId, kWh: totalKWh * (shares[i] ?? 0), factor: r.factor })),
  };
}

function lineFor(eq: Equipment, input: Equipment["inputs"][number], scenario: Scenario, ctx: CalcContext, mixEf: number, issues: Issue[]): CalcLine {
  const base: CalcLine = {
    inputId: input.id, key: input.key, label: input.label, kind: input.kind, amount: input.amount, unit: input.unit,
    valueType: input.valueType, source: input.source, converted: null, convertedUnit: null, ef: null, kgCO2eq: 0, formula: "",
  };
  if (input.kind === "parameter") {
    return { ...base, excluded: "parameter", formula: "참고값, 계산에 쓰지 않음" };
  }
  if (input.amount === null || input.valueType === "missing") {
    if (input.required) {
      issues.push({ level: "error", code: "MISSING_REQUIRED", message: `${eq.name}, ${input.label}: 필수값이 비어 있습니다`, equipmentId: eq.id, inputId: input.id, step: "inputs" });
    } else {
      issues.push({ level: "info", code: "EXCLUDED", message: `${eq.name}, ${input.label}: 자료 미입력(선택 항목), 계산 제외`, equipmentId: eq.id, inputId: input.id, step: "inputs" });
    }
    return { ...base, excluded: "missing", formula: "자료 미입력" };
  }
  if (input.amount < 0) {
    issues.push({ level: "error", code: "NEGATIVE", message: `${eq.name} , ${input.label}: 음수는 입력할 수 없습니다`, equipmentId: eq.id, inputId: input.id, step: "inputs" });
    return { ...base, excluded: "negative", formula: "음수, 계산 제외" };
  }

  // 배출계수
  let ef = input.kind === "electricity" && !input.customEf && !input.efId
    ? { id: "mix", name: "전력 믹스 계수", value: mixEf, unit: "kgCO2eq/kWh", source: "전력원별 원본 계수와 비율로 합성. 원본 출처·대표연도는 전력 믹스 구성 계수 참조", baseYear: 0, status: "composed", composed: true }
    : resolveFactor(input, ctx.factors, scenario.method);
  if (!ef) {
    issues.push({ level: "info", code: "NO_EF", message: `${eq.name}, ${input.label}: 배출계수가 없어 LCI 에만 기록하고 계산에서 제외합니다`, equipmentId: eq.id, inputId: input.id, step: "inputs" });
    return { ...base, excluded: "no_ef", formula: "배출계수 없음, LCI 기록, 계산 제외" };
  }
  const denom = denominatorUnit(ef.unit);
  const k = denom ? conversionFactor(input.unit, denom) : null;
  if (denom === null || k === null) {
    issues.push({ level: "error", code: "UNIT_MISMATCH", message: `${eq.name} , ${input.label}: 입력 단위(${input.unit})를 배출계수 단위(${ef.unit})로 환산할 수 없습니다`, equipmentId: eq.id, inputId: input.id, step: "inputs" });
    return { ...base, ef, excluded: "unit", formula: "단위 오류" };
  }
  const converted = input.amount * k;
  const kg = converted * ef.value;
  const conv = k === 1 ? "" : ` (= ${fmt(converted)} ${denom})`;
  return {
    ...base, converted, convertedUnit: denom, ef, kgCO2eq: kg,
    formula: `${fmt(input.amount)} ${input.unit}${conv} × ${fmt(ef.value, 4)} ${ef.unit} = ${fmt(kg)} kgCO2eq`,
  };
}

export function calculate(rawScenario: Scenario, ctx: CalcContext): CalcResult {
  const issues: Issue[] = [];
  const parsed = ScenarioSchema.safeParse(rawScenario);
  if (!parsed.success) {
    for (const e of parsed.error.issues.slice(0, 20)) {
      issues.push({ level: "error", code: "SCHEMA", message: `${e.path.join(".")}: ${e.message}` });
    }
  }
  const scenario = parsed.success ? parsed.data : rawScenario;
  const method = ctx.methods.find((m) => m.id === scenario.method);
  if (!method) {
    issues.push({ level: "error", code: "METHOD_UNKNOWN", message: `산정방식 설정이 없습니다: ${scenario.method}`, step: "method" });
  }
  const m: MethodConfig = method ?? {
    id: scenario.method, label: scenario.method, description: "", standards: [], gwp: { basis: "IPCC AR5 100년", co2: 1, ch4: 28, n2o: 265 },
    functionalUnit: { locked: false, h2Kg: 1 }, boundary: { locked: false, description: "", includeLocations: ["upstream", "onsite", "downstream"] },
    denominator: "delivered", notices: [],
  };

  const ordered = [...scenario.equipment].sort((a, b) => a.order - b.order);
  const mismatched = mismatchedProductionEquipment(scenario);
  if (mismatched.length) {
    const review = scenario.basis.technologyReview;
    const reviewed = review?.productionTechId === scenario.basis.productionTechId && Boolean(review.note.trim()) && mismatched.every((e) => review.equipmentIds.includes(e.id));
    issues.push({ level: reviewed ? "warning" : "error", code: "TECHNOLOGY_MISMATCH", step: "basis", message: `선택한 생산방식과 다른 기술의 설비: ${mismatched.map((e) => e.name).join(", ")}. ${reviewed ? `유지 검토 근거: ${review!.note}` : "기본조건에서 설비를 교체하거나 유지 근거를 기록하세요"}` });
  }

  // 1) 경계 판정 + 전력 총량(믹스 계수 합성에 필요)
  const boundary = new Map(ordered.map((eq) => [eq.id, effectiveInBoundary(eq, m)] as const));
  let totalKWh = 0;
  for (const eq of ordered) {
    if (!boundary.get(eq.id)?.inBoundary) continue;
    for (const inp of eq.inputs) {
      if (inp.kind === "electricity" && inp.amount !== null && inp.amount > 0) {
        const k = conversionFactor(inp.unit, "kWh");
        if (k !== null) totalKWh += inp.amount * k;
      }
    }
  }
  const electricity = composeElectricity(scenario, ctx, issues, totalKWh);

  // 2) 인증제 규칙 , 기능단위 잠금,순도
  if (m.functionalUnit.locked) {
    if (scenario.functionalUnit.h2Kg !== m.functionalUnit.h2Kg) {
      issues.push({ level: "warning", code: "CERT_FU_LOCKED", message: `인증제 모의산정의 기능단위는 수소 ${m.functionalUnit.h2Kg} kg 으로 고정됩니다 (입력값 ${scenario.functionalUnit.h2Kg} kg 무시)`, step: "fu" });
    }
  }

  // 3) 설비별 계산
  const byEquipment: EquipmentResult[] = [];
  for (const eq of ordered) {
    const b = boundary.get(eq.id) ?? { inBoundary: eq.inBoundary, forcedOut: false };
    if (b.forcedOut) {
      issues.push({ level: "info", code: "CERT_SCOPE_EXCLUDED", message: `${eq.name}: 생산시설 출하 이후 설비라 인증제 산정범위에서 제외됩니다`, equipmentId: eq.id, step: "boundary" });
    }
    if (!b.inBoundary && !b.forcedOut) {
      if (m.boundary.locked) {
        issues.push({ level: "error", code: "CERT_REQUIRED_PROCESS", message: `${eq.name}: 잠긴 산정 경계 안의 설비가 제외되어 있습니다. 설비를 포함해야 최종 결과와 인증 등급을 산출할 수 있습니다. 제외 사유: ${eq.exclusionReason ?? "미기록"}, 근거: ${eq.exclusionNote?.trim() || "미기록"}`, equipmentId: eq.id, step: "boundary" });
      } else if (!eq.exclusionNote?.trim()) {
        issues.push({ level: "warning", code: "EXCLUSION_EVIDENCE", message: `${eq.name}: 설비 제외 근거가 없습니다. 시스템 경계와 기여도를 검토하고 사유 메모를 기록하세요`, equipmentId: eq.id, step: "boundary" });
      }
    }
    const lines = b.inBoundary
      ? eq.inputs.map((inp) => lineFor(eq, inp, scenario, ctx, electricity.mixEf, issues))
      : eq.inputs.map<CalcLine>((inp) => ({
          inputId: inp.id, key: inp.key, label: inp.label, kind: inp.kind, amount: inp.amount, unit: inp.unit, valueType: inp.valueType,
          source: inp.source, converted: null, convertedUnit: null, ef: null, kgCO2eq: 0, formula: "시스템 경계 밖, 계산 제외",
        }));
    byEquipment.push({
      equipmentId: eq.id, name: eq.name, stage: eq.stage, location: eq.location, order: eq.order,
      inBoundary: b.inBoundary, forcedOut: b.forcedOut,
      exclusionReason: b.forcedOut ? "cert_scope" : b.inBoundary ? undefined : (eq.exclusionReason ?? "other"),
      lines, kgCO2eq: lines.reduce((a, l) => a + l.kgCO2eq, 0), share: 0,
      h2: { inKg: eq.h2.inKg, outKg: eq.h2.outKg, lossKg: eq.h2.lossKg },
    });
  }

  // 4) 수소 흐름 , 생산량 → 출하량 → 최종 인도량
  let current: number | null = null;
  let producedKg = 0;
  let gateKg: number | null = null;
  let lossKg = 0;
  let hasProducer = false;
  for (const eq of ordered) {
    const b = boundary.get(eq.id);
    if (!b?.inBoundary) continue;
    if (eq.h2.role === "producer") {
      hasProducer = true;
      if (eq.h2.outKg === null) {
        issues.push({ level: "error", code: "MISSING_REQUIRED", message: `${eq.name}: 수소 생산량(kg)이 비어 있습니다`, equipmentId: eq.id, step: "inputs" });
        continue;
      }
      producedKg += eq.h2.outKg;
      current = (current ?? 0) + eq.h2.outKg;
      if (eq.location !== "downstream") gateKg = current;
    } else if (eq.h2.role === "passthrough") {
      const { inKg, outKg, lossKg: l } = eq.h2;
      if (inKg !== null && current !== null && Math.abs(inKg - current) > MASS_TOL * Math.max(current, 1)) {
        issues.push({ level: "error", code: "MASS_BALANCE", message: `${eq.name}: 투입 수소량 ${fmt(inKg)} kg 이 앞 설비의 산출량 ${fmt(current)} kg 과 다릅니다`, equipmentId: eq.id, step: "inputs" });
      }
      const available = inKg ?? current;
      if (available !== null && outKg !== null && l !== null && Math.abs(available - outKg - l) > MASS_TOL * Math.max(available, 1)) {
        issues.push({ level: "error", code: "MASS_BALANCE", message: `${eq.name}: 투입(${fmt(available)}) ≠ 산출(${fmt(outKg)}) + 손실(${fmt(l)}) kg`, equipmentId: eq.id, step: "inputs" });
      }
      if (available !== null && outKg !== null && outKg - available > MASS_TOL * Math.max(available, 1) && l === null) {
        issues.push({ level: "error", code: "MASS_BALANCE", message: `${eq.name}: 수소를 생산하지 않는 설비의 산출량 ${fmt(outKg)} kg이 투입량 ${fmt(available)} kg보다 큽니다`, equipmentId: eq.id, step: "inputs" });
      }
      if (available !== null && l !== null && l > available) {
        issues.push({ level: "error", code: "MASS_BALANCE", message: `${eq.name}: 수소 손실량이 투입량보다 큽니다`, equipmentId: eq.id, step: "inputs" });
      }
      if (outKg === null && l === null) {
        issues.push({ level: "error", code: "MISSING_REQUIRED", message: `${eq.name}: 산출 수소량 또는 손실량을 입력해야 흐름을 확인할 수 있습니다`, equipmentId: eq.id, step: "inputs" });
      }
      if (available === null) issues.push({ level: "error", code: "MASS_BALANCE", message: `${eq.name}: 앞 공정의 수소 흐름 또는 투입량이 없어 물질수지를 확인할 수 없습니다`, equipmentId: eq.id, step: "inputs" });
      if (l !== null) lossKg += l;
      if (outKg !== null) current = outKg;
      else if (inKg !== null && l !== null) current = inKg - l;
      else if (current !== null && l !== null) current = current - l;
      if (eq.location !== "downstream") gateKg = current;
    }
  }
  if (!hasProducer) issues.push({ level: "error", code: "NO_PRODUCER", message: "시스템 경계 안에 수소를 생산하는 설비가 없습니다", step: "equipment" });
  const deliveredKg = current ?? 0;
  const gate = gateKg ?? producedKg;
  const denomKind = m.denominator;
  const denomKg = denomKind === "gate" ? gate : deliveredKg;
  if (hasProducer && denomKg <= 0) issues.push({ level: "error", code: "DENOMINATOR_ZERO", message: "기능단위당 배출량의 분모(수소량)가 0 입니다", step: "inputs" });

  // 5) 합산,배분
  const gross = byEquipment.reduce((a, e) => a + e.kgCO2eq, 0);
  let allocFactor = 1;
  let allocNote = "부산물 배분 없음. 모든 배출을 수소에 귀속";
  const bps = scenario.allocation.byproducts;
  if (scenario.allocation.policy === "mass" && bps.length) {
    const bpMass = bps.reduce((a, b) => a + b.amountKg, 0);
    allocFactor = producedKg / (producedKg + bpMass);
    allocNote = `질량 기준 배분: 수소 ${fmt(producedKg)} kg / (수소 + 부산물 ${fmt(bpMass)} kg) = ${fmt(allocFactor, 4)}`;
  } else if (scenario.allocation.policy === "energy" && bps.length) {
    const h2E = producedKg * H2_LHV_MJ_PER_KG;
    const bpE = bps.reduce((a, b) => a + b.amountKg * b.lhvMJperKg, 0);
    allocFactor = h2E / (h2E + bpE);
    allocNote = `에너지 기준 배분: 수소 ${fmt(h2E)} MJ / (수소 + 부산물 ${fmt(bpE)} MJ) = ${fmt(allocFactor, 4)}`;
  }
  // 배분은 생산시설(상류,내부) 배출에만 적용 , 부산물은 생산시설에서 갈라져 나간다
  const allocated = byEquipment.reduce((a, e) => a + e.kgCO2eq * (e.location === "downstream" ? 1 : allocFactor), 0);
  const total = allocated;
  for (const e of byEquipment) {
    const f = e.location === "downstream" ? 1 : allocFactor;
    e.kgCO2eq = e.kgCO2eq * f;
    for (const l of e.lines) l.kgCO2eq = l.kgCO2eq * f;
    e.share = total > 0 ? e.kgCO2eq / total : 0;
  }
  const byStage = STAGES.map((stage: Stage) => {
    const kg = byEquipment.filter((e) => e.stage === stage).reduce((a, e) => a + e.kgCO2eq, 0);
    return { stage, kgCO2eq: kg, share: total > 0 ? kg / total : 0 };
  });

  const hasError = issues.some((i) => i.level === "error");
  const fuKg = m.functionalUnit.locked ? m.functionalUnit.h2Kg : scenario.functionalUnit.h2Kg;
  const perKg = !hasError && denomKg > 0 ? total / denomKg : null;
  const perFunctionalUnit = perKg === null ? null : perKg * fuKg;

  // 6) 기여도
  const topEquipment: Contributor[] = [...byEquipment]
    .filter((e) => e.inBoundary && e.kgCO2eq > 0)
    .sort((a, b) => b.kgCO2eq - a.kgCO2eq)
    .slice(0, 5)
    .map((e) => ({ kind: "equipment", id: e.equipmentId, label: e.name, kgCO2eq: e.kgCO2eq, share: e.share, equipmentId: e.equipmentId }));
  const topInputs: Contributor[] = byEquipment
    .flatMap((e) => e.lines.filter((l) => l.kgCO2eq > 0).map((l) => ({ kind: "input" as const, id: l.inputId, label: `${e.name}, ${l.label}`, kgCO2eq: l.kgCO2eq, share: total > 0 ? l.kgCO2eq / total : 0, equipmentId: e.equipmentId })))
    .sort((a, b) => b.kgCO2eq - a.kgCO2eq)
    .slice(0, 5);

  // 7) 인증제 판정
  let cert: CertResult | null = null;
  if (m.id === "cert") {
    const reasons: string[] = hasError ? ["입력 오류가 있어 최종 배출원단위와 인증 등급을 산출하지 않습니다"] : [];
    const purityMin = m.functionalUnit.purityMinPct;
    if (purityMin !== undefined && scenario.functionalUnit.purityPct < purityMin) {
      reasons.push(`수소 순도 ${scenario.functionalUnit.purityPct}% 가 인증제 적용조건(${purityMin}% 이상)을 충족하지 않습니다`);
      issues.push({ level: "warning", code: "CERT_PURITY", message: `인증제 모의산정 조건 미충족: 수소 순도 ${scenario.functionalUnit.purityPct}% < ${purityMin}%`, step: "fu" });
    }
    const renewableShare = electricity.sources.reduce((a, s) => a + (ctx.sources.find((x) => x.id === s.sourceId)?.certRenewable ? s.share : 0), 0);
    const grades = m.grades ?? [];
    let grade: number | null = null;
    if (perKg !== null && reasons.length === 0) {
      const g = grades.find((x) => perKg <= x.maxKgCO2eq);
      grade = g ? g.grade : null;
    }
    cert = { eligible: reasons.length === 0, reasons, grade, thresholdKgCO2eq: m.thresholdKgCO2eq ?? null, grades, renewableShare };
  }

  // 8) 계산과정
  const steps: CalcStep[] = [];
  steps.push({
    title: "① 전력 믹스 배출계수",
    lines: [
      ...electricity.sources.map((s) => `${s.label}: ${pct(s.share)} × ${fmt(s.ef, 4)} kgCO2eq/kWh`),
      `합성 계수 = ${fmt(electricity.mixEf, 4)} kgCO2eq/kWh (경계 내 전력 사용량 ${fmt(totalKWh)} kWh)`,
    ],
  });
  for (const e of byEquipment) {
    if (!e.inBoundary) {
      steps.push({ title: `${STAGE_LABEL[e.stage]}, ${e.name}`, lines: [e.forcedOut ? "인증제 산정범위 밖, 계산 제외" : `시스템 경계 밖(${e.exclusionReason ?? "제외"}), 계산 제외`] });
      continue;
    }
    steps.push({ title: `${STAGE_LABEL[e.stage]}, ${e.name}`, lines: [...e.lines.map((l) => `${l.label}: ${l.formula}`), `소계 = ${fmt(e.kgCO2eq)} kgCO2eq`] });
  }
  steps.push({
    title: "② 합산과 배분",
    lines: [
      `설비별 합계(배분 전) = ${fmt(gross)} kgCO2eq`,
      allocNote,
      `총 온실가스 배출량 = ${fmt(total)} kgCO2eq`,
    ],
  });
  steps.push({
    title: "③ 수소량과 기능단위당 배출량",
    lines: [
      `생산량 ${fmt(producedKg)} kg → 출하지점 ${fmt(gate)} kg → 최종 인도 ${fmt(deliveredKg)} kg (손실 합계 ${fmt(lossKg)} kg)`,
      `분모 = ${denomKind === "gate" ? "생산시설 출하지점 수소량" : "최종 인도 수소량"} ${fmt(denomKg)} kg`,
      perKg === null
        ? "필수값 누락 또는 오류가 있어 최종 결과를 산출하지 않습니다"
        : `1 kg당 배출량 = ${fmt(total)} kgCO2eq ÷ ${fmt(denomKg)} kg = ${fmt(perKg, 3)} kgCO2eq/kgH2`,
      perFunctionalUnit === null ? "기능단위당 배출량: 계산 제한" : `기능단위 수소 ${fmt(fuKg)} kg의 배출량 = ${fmt(perKg, 3)} × ${fmt(fuKg)} = ${fmt(perFunctionalUnit, 3)} kgCO2eq/FU`,
    ],
  });

  return {
    status: hasError ? "blocked" : "ok",
    issues,
    method: m.id,
    methodLabel: m.label,
    standards: m.standards,
    gwpBasis: m.gwp.basis,
    batch: { producedKg, gateKg: gate, deliveredKg, lossKg },
    denominator: { kind: denomKind, kg: denomKg },
    functionalUnitKg: fuKg,
    grossKgCO2eq: gross,
    allocation: { policy: scenario.allocation.policy, factor: allocFactor, note: allocNote },
    totalKgCO2eq: total,
    perKgH2: perKg,
    perFunctionalUnitKgCO2eq: perFunctionalUnit,
    byEquipment,
    byStage,
    topEquipment,
    topInputs,
    electricity,
    cert,
    steps,
    notices: m.notices,
  };
}

/** 계산 없이 문제만 보고 싶을 때 */
export function validate(scenario: Scenario, ctx: CalcContext): Issue[] {
  return calculate(scenario, ctx).issues;
}

export { toApplied };
